/**
 * ffmpegProbe.js
 *
 * Lightweight stream probing helper that runs the bundled ffmpeg binary with no output,
 * captures its stderr "banner" output, and parses the per-stream descriptions into
 * structured track objects. We use this instead of ffprobe because `ffmpeg-static-electron`
 * only bundles `ffmpeg.exe` (no `ffprobe.exe`).
 *
 * ffmpeg always exits with a non-zero exit code when no output is specified, so the
 * promisified `child_process.exec` rejection is the expected success path and the
 * banner text lives on `err.stderr`.
 */

const exec = typeof window !== "undefined" ? window.exec : null;
const rawFfmpegPath =
    typeof window !== "undefined" && window.ffmpegPath
        ? window.ffmpegPath.replace("app.asar", "app.asar.unpacked")
        : null;

const STREAM_LINE_RE =
    /Stream\s+#(\d+):(\d+)(?:\[[^\]]+\])?(?:\(([^)]+)\))?:\s+(Video|Audio|Subtitle|Data|Attachment)\s*:\s*([^,\n]+)((?:,\s*[^,\n]+)*)/i;
const DURATION_RE = /Duration:\s*(\d+):(\d+):([\d.]+)/i;

function parseAudioMeta(extraParts) {
    let sampleRate = null;
    let layout = null;
    let channels = null;

    for (const rawPart of extraParts) {
        const part = rawPart.trim();
        if (!part) continue;

        if (/^\d+\s*Hz$/i.test(part)) {
            sampleRate = part;
            continue;
        }

        if (/^(mono|stereo|quad|hexagonal)$/i.test(part)) {
            layout = part.toLowerCase();
            channels =
                layout === "mono"
                    ? 1
                    : layout === "stereo"
                      ? 2
                      : layout === "quad"
                        ? 4
                        : layout === "hexagonal"
                          ? 6
                          : null;
            continue;
        }

        const surroundMatch = part.match(/^(\d)\.(\d)(?:\(side\)|\(back\))?$/);
        if (surroundMatch) {
            layout = part;
            channels = parseInt(surroundMatch[1], 10) + parseInt(surroundMatch[2], 10);
            continue;
        }

        const channelsMatch = part.match(/^(\d+)\s*channels?$/i);
        if (channelsMatch) {
            channels = parseInt(channelsMatch[1], 10);
            layout = layout || `${channels} channels`;
            continue;
        }
    }

    return { sampleRate, layout, channels };
}

function describeTrack(track) {
    if (track.type === "audio") {
        const bits = [`Audio ${track.streamIdx}`, track.codec];
        if (track.layout) bits.push(track.layout);
        if (track.language) bits.push(`[${track.language}]`);
        return bits.join(" \u2022 ");
    }
    if (track.type === "video") {
        return `Video ${track.streamIdx} \u2022 ${track.codec}`;
    }
    return `${track.type[0].toUpperCase()}${track.type.slice(1)} ${track.streamIdx} \u2022 ${track.codec}`;
}

/**
 * Run ffmpeg with no output to obtain its stream banner.
 * Returns the raw stderr text so callers can show it on failure for debugging.
 */
async function readBanner(filePath) {
    if (!exec || !rawFfmpegPath) {
        throw new Error("ffmpeg probe is only available in the desktop runtime");
    }
    const cmd = `"${rawFfmpegPath}" -hide_banner -i "${filePath}"`;
    try {
        const { stderr } = await exec(cmd);
        return stderr || "";
    } catch (err) {
        if (err && typeof err.stderr === "string" && err.stderr.length > 0) {
            return err.stderr;
        }
        throw err;
    }
}

/**
 * Probe a media file for its tracks. Returns:
 *   { filePath, tracks: Track[], duration: number, raw: string }
 *
 * Track shape:
 *   {
 *     inputIdx: 0,            // ffmpeg-reported input index (always 0 because we probe one file at a time)
 *     streamIdx: number,      // 0-based stream index within the file
 *     type: 'video'|'audio'|'subtitle'|'data'|'attachment',
 *     codec: string,
 *     language: string|null,
 *     layout: string|null,
 *     channels: number|null,
 *     sampleRate: string|null,
 *     label: string,          // human-readable label for dropdowns
 *   }
 */
export async function probeTracks(filePath) {
    if (!filePath) {
        throw new Error("probeTracks requires a file path");
    }

    const stderr = await readBanner(filePath);
    const lines = stderr.split(/\r?\n/);
    const tracks = [];
    let duration = 0;

    for (const line of lines) {
        const dMatch = line.match(DURATION_RE);
        if (dMatch) {
            duration =
                parseInt(dMatch[1], 10) * 3600 +
                parseInt(dMatch[2], 10) * 60 +
                parseFloat(dMatch[3]);
        }

        const sMatch = line.match(STREAM_LINE_RE);
        if (!sMatch) continue;

        const type = sMatch[4].toLowerCase();
        const codec = sMatch[5].trim().split(/\s+/)[0];
        const extraParts = (sMatch[6] || "")
            .split(",")
            .map((part) => part.trim())
            .filter(Boolean);

        const audioMeta = type === "audio" ? parseAudioMeta(extraParts) : {};

        const track = {
            inputIdx: parseInt(sMatch[1], 10),
            streamIdx: parseInt(sMatch[2], 10),
            language: sMatch[3] || null,
            type,
            codec,
            layout: audioMeta.layout || null,
            channels: audioMeta.channels || null,
            sampleRate: audioMeta.sampleRate || null,
        };
        track.label = describeTrack(track);
        tracks.push(track);
    }

    return { filePath, tracks, duration, raw: stderr };
}

export function audioTracksOf(probeResult) {
    return (probeResult?.tracks || []).filter((t) => t.type === "audio");
}

export function passthroughTracksOf(probeResult) {
    return (probeResult?.tracks || []).filter(
        (t) => t.type === "video" || t.type === "subtitle" || t.type === "data" || t.type === "attachment"
    );
}
